import React, { useState } from 'react'

import { useToast } from '@zet/common/components/toast'
import { i18n } from '@zet/localization'
import { navigateTo } from '@zet/utils/navigation'
import {
  CustomBottomSheet,
  PaymentFailure,
  UnlockCategoryProps,
  useSubscriptionStore,
} from '@zet/zet-prime'

import {
  initiatePayment,
  retrySubscriptionStatus,
} from '../../api/landingPage/functions'
import {
  useSubscribeZetPrime,
  useSubscriptionInfo,
  useSubscriptionStatus,
} from '../../api/landingPage/hooks'
import { SUBSCRIPTION_STATUS } from '../../utils/constants'

const UnlockCategoryBottomSheet = ({
  isVisible,
  onClose,
  categoryName,
}: UnlockCategoryProps) => {
  const subscription = useSubscriptionStore((state: any) => state.subscription)
  const { mutate: subscribeZetPrime } = useSubscribeZetPrime()
  const [isLoading, setIsLoading] = useState(false)
  const [isPaymentFailureVisible, setIsPaymentFailureVisible] = useState(false)
  const { refetch } = useSubscriptionInfo()
  const { mutate: getSubscriptionStatus } = useSubscriptionStatus()
  const toast = useToast()

  const handleSubscribe = () => {
    setIsLoading(true)
    subscribeZetPrime(subscription?.status === SUBSCRIPTION_STATUS.EXPIRED, {
      onSuccess: async (data: any) => {
        if (data?.success && data?.token) {
          try {
            await initiatePayment(data?.token)
            // Retry subscription status API 3 times with 3-second intervals
            await retrySubscriptionStatus(
              data?.orderId,
              getSubscriptionStatus,
              refetch,
            )
            // Payment successful
            setIsLoading(false)
            onClose()
            navigateTo('ZetPrimeSuccessScreen')
          } catch (error: any) {
            setIsLoading(false)
            onClose()
            // Show payment failure bottom sheet after all retries failed
            setIsPaymentFailureVisible(true)
          }
        }
      },
      onError: (error: unknown) => {
        onClose()
        setIsLoading(false)
        toast?.show({
          intent: 'error',
          heading: i18n.t('toastMessages.error'),
          description:
            (error?.response?.data?.error?.message as string) ??
            i18n.t('toastMessages.somethingWentWrong'),
        })
      },
    })
  }

  const handleCancelSubscription = () => {
    // TODO: Implement cancel subscription logic
    navigateTo('ZetPrime')
    onClose()
  }

  return (
    <>
      <CustomBottomSheet
        isVisible={isVisible}
        onClose={onClose}
        iconImage={require('@zet/zet-prime/assets/images/lock.png')}
        headerText={i18n.t('zetPrime.unlockCategoryHeader', {
          categoryName,
        })}
        subHeaderText={i18n.t('zetPrime.unlockCategorySubHeader', {
          categoryName,
        })}
        headerSecondaryText={i18n.t('zetPrime.ZetPrimeAt3')}
        footerText={i18n.t('zetPrime.billedMonthlyCancelAnytime')}
        earningsWithZetPrime={subscription?.earnings}
        buttons={[
          {
            text: i18n.t('zetPrime.continueEarnings'),
            onPress: handleSubscribe,
            variant: 'primary',
            loading: isLoading,
          },
          {
            text: i18n.t('zetPrime.knowMore'),
            onPress: handleCancelSubscription,
            className: 'bg-primary-10 text-primary-100',
            textStyle: 'text-primary-100',
          },
        ]}
      />

      <PaymentFailure
        isVisible={isPaymentFailureVisible}
        onClose={() => setIsPaymentFailureVisible(false)}
        onRetry={handleSubscribe}
      />
    </>
  )
}

export default UnlockCategoryBottomSheet
